import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import Button from '../common/Button';

const ContactSection = () => {
  // Direct embed URL with specific parameters to hide details
  const calendlyUrl = "https://calendly.com/amvlaw-info/30min?embed_domain=veritect.ai&embed_type=Inline&hide_gdpr_banner=1&hide_landing_page_details=1&hide_event_type_details=1";
  const [sectionRef, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });
  
  // State to manage iframe loading and height
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(600); // Increased default height
  const iframeRef = useRef(null);
  
  // References for tracking elements to clean up
  const styleTagsRef = useRef([]);
  const observersRef = useRef([]);
  const intervalsRef = useRef([]);
  const timeoutsRef = useRef([]);

  // Helper function to safely clear all references on unmount
  const cleanupReferences = () => {
    // Clear style tags
    styleTagsRef.current.forEach(styleTag => {
      try {
        if (styleTag && styleTag.parentNode) {
          styleTag.parentNode.removeChild(styleTag);
        }
      } catch (e) {
        console.log("Error removing style tag:", e);
      }
    });
    
    // Disconnect observers
    observersRef.current.forEach(observer => {
      try {
        if (observer && observer.disconnect) {
          observer.disconnect();
        }
      } catch (e) {
        console.log("Error disconnecting observer:", e);
      }
    });
    
    // Clear intervals
    intervalsRef.current.forEach(interval => {
      try {
        clearInterval(interval);
      } catch (e) {
        console.log("Error clearing interval:", e);
      }
    });
    
    // Clear timeouts
    timeoutsRef.current.forEach(timeout => {
      try {
        clearTimeout(timeout);
      } catch (e) {
        console.log("Error clearing timeout:", e);
      }
    });
  };

  // Add custom CSS to hide elements directly
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      #demo-iframe {
        border: none;
        width: 100%;
        background: white;
        overflow: hidden;
        border-radius: 0 0 0.75rem 0.75rem;
      }
      
      /* Hide loading indicator when iframe is loaded */
      .iframe-loaded .iframe-loading {
        display: none;
      }
      
      /* Remove extra white space at the bottom */
      .calendly-inline-widget {
        height: auto !important;
      }
      
      /* Remove scrollbars from the calendar container */
      .calendar-container {
        overflow: hidden !important;
      }
    `;
    document.head.appendChild(style);
    styleTagsRef.current.push(style);
    
    // Set up a window resize listener to adjust iframe height
    const handleResize = () => {
      adjustIframeHeight();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      cleanupReferences();
    };
  }, []);
  
  // Function to adjust iframe height and remove top section
  const adjustIframeHeight = () => {
    if (iframeRef.current) {
      try {
        // Try to access iframe content if possible
        const iframeDocument = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
        
        if (iframeDocument && iframeDocument.body) {
          // Target and remove the 30 Minute Meeting information section
          const infoSections = iframeDocument.querySelectorAll('.meeting-type-information, [data-component="MeetingType/Information"], .heading-section, .event-type-details');
          infoSections.forEach(section => {
            try {
              if (section.parentNode) {
                section.parentNode.removeChild(section);
              }
            } catch (e) {
              section.style.display = 'none';
              section.style.height = '0';
              section.style.overflow = 'hidden';
            }
          });

          // Also remove by content - target anything with "30" and "min" text
          const allElements = iframeDocument.querySelectorAll('div, h1, h2, h3, p, span');
          allElements.forEach(el => {
            const text = el.textContent || '';
            if (
              (text.includes('30') && text.includes('min')) || 
              text.includes('Minute Meeting') || 
              text.includes('Web conferencing') ||
              text.includes('info info')
            ) {
              // Look for parent containers to remove
              let parent = el;
              // Go up to 3 levels to find a suitable container
              for (let i = 0; i < 3; i++) {
                if (parent.parentNode && 
                    parent.parentNode !== iframeDocument.body && 
                    parent.parentNode.tagName !== 'BODY') {
                  parent = parent.parentNode;
                } else {
                  break;
                }
              }
              
              try {
                parent.style.display = 'none';
                parent.style.height = '0';
                parent.style.overflow = 'hidden';
              } catch (e) {
                // Fallback if we can't style the parent
                el.style.display = 'none';
              }
            }
          });
          
          // Find the calendar content for height calculation
          const calendarElement = iframeDocument.querySelector('.calendar-component, .calendar, .date-picker-component, [data-component="DatePicker"]');
          const timezoneSelector = iframeDocument.querySelector('.timezone-picker, .timezone-selector');
          const timeSlots = iframeDocument.querySelector('.spots-container, [data-component="AvailableTimes/SpotList"]');
          
          if (calendarElement) {
            // Calculate the height needed for calendar, timezone and time slots
            const calendarHeight = calendarElement.offsetHeight || 400;
            const timezoneSelectorHeight = timezoneSelector ? timezoneSelector.offsetHeight + 10 : 50;
            const timeSlotsHeight = timeSlots ? timeSlots.offsetHeight + 30 : 150; // Add extra space for time slots
            const padding = 40; // Additional padding
            
            // Calculate total height needed
            const totalHeight = calendarHeight + timezoneSelectorHeight + timeSlotsHeight + padding;
            
            // Set the iframe height
            setIframeHeight(totalHeight);
            iframeRef.current.style.height = `${totalHeight}px`;
          }
        }
      } catch (e) {
        // Cross-origin restrictions prevent accessing iframe content
        console.log("Cross-origin restrictions prevent content access:", e);
        // Use a default height for the iframe based on screen size
        const viewportHeight = window.innerHeight;
        const defaultHeight = Math.min(650, viewportHeight * 0.7);
        setIframeHeight(defaultHeight);
        if (iframeRef.current) {
          iframeRef.current.style.height = `${defaultHeight}px`;
        }
      }
    }
  };

  // Handle iframe load event
  const handleIframeLoad = () => {
    setIframeLoaded(true);
    
    // Apply styles and adjustments immediately
    const applyAdjustments = () => {
      // Run adjustment to resize and clean up
      adjustIframeHeight();
      
      // Try to access iframe content if possible
      try {
        if (iframeRef.current && iframeRef.current.contentWindow) {
          const iframeDocument = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
          
          if (iframeDocument) {
            const style = document.createElement('style');
            style.textContent = `
              /* Hide unwanted elements */
              .calendly-powered-by, 
              .cookie-banner,
              .cookie-settings,
              .gdpr-banner,
              .header,
              .footer,
              .MeetingType__header,
              .MeetingType__information,
              .meetingtype-information,
              .poweredBy,
              [data-component="MeetingType/Information"],
              .meeting-type-information,
              .event-type-details,
              .heading-section,
              .event-type-details,
              div:has(> span:contains("30 min")),
              div:has(> span:contains("Web conferencing")),
              div:has(> span:contains("info info")),
              .InlineWidget__footer,
              p:contains("30 min"),
              span:contains("30 min"),
              h1:contains("30 Minute Meeting"),
              h2:contains("30 Minute Meeting"),
              h3:contains("30 Minute Meeting"),
              p:contains("Web conferencing"),
              h1.main-heading-title {
                display: none !important;
                height: 0 !important;
                overflow: hidden !important;
              }
              
              /* Custom styles for calendar elements */
              body {
                margin: 0 !important;
                padding: 0 !important;
                overflow-x: hidden !important;
              }
              
              /* Style the calendar page */
              .main-content-area {
                padding-top: 0 !important;
                margin-top: 0 !important;
              }
              
              /* Style the date picker */
              .date-picker button:hover,
              .calendar button:hover {
                background-color: rgba(59, 130, 246, 0.1) !important;
              }
              
              .date-picker button.selected,
              .calendar button.selected {
                background-color: #3b82f6 !important;
                color: white !important;
              }
              
              /* Style primary buttons to match your theme */
              .calendly-page button.blue, 
              .calendly-page .btn-primary,
              button.submit,
              button[type="submit"] {
                background-color: #3b82f6 !important;
                border-color: #3b82f6 !important;
                color: white !important;
              }
              
              .calendly-page button.blue:hover, 
              .calendly-page .btn-primary:hover,
              button.submit:hover,
              button[type="submit"]:hover {
                background-color: #2563eb !important;
                border-color: #2563eb !important;
              }
              
              /* Style time slots */
              .spot {
                border-radius: 8px !important;
                transition: all 0.2s ease-in-out !important;
              }
              
              .spot:hover {
                transform: translateY(-2px) !important;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
              }
              
              /* Fix spacing issues */
              .InlineWidget__mainContentArea {
                padding-top: 0 !important;
              }
              
              .calendly-page {
                padding-top: 0 !important;
              }
              
              /* Fix title spacing */
              .section-header {
                margin-top: 0 !important;
                padding-top: 0 !important;
              }
              
              /* Style the header text */
              .section-header h1, .section-header .main-heading-title {
                font-size: 1.5rem !important;
                color: #1f2937 !important;
                margin-top: 0 !important;
                margin-bottom: 1rem !important;
              }
              
              /* Ensure calendar months are styled properly */
              .month-header {
                color: #1f2937 !important;
                font-weight: 600 !important;
              }
              
              /* Style time slots */
              .spot-container {
                margin-bottom: 8px !important;
              }
              
              .spots-container {
                margin-top: 16px !important;
              }
              
              /* Add a header for time selection */
              .spots-container::before {
                content: "Select a Time" !important;
                display: block !important;
                font-size: 1.25rem !important;
                font-weight: 600 !important;
                color: #1f2937 !important;
                text-align: center !important;
                margin-bottom: 1rem !important;
              }
              
              /* Make time slots look nicer */
              .spot .time,
              .spot .timezone {
                font-weight: 500 !important;
                color: #1f2937 !important;
              }
            `;
            
            try {
              iframeDocument.head.appendChild(style);
              styleTagsRef.current.push(style);
            } catch (e) {
              console.log("Could not append style to iframe head:", e);
            }
            
            // Directly target and remove the top info section if we can
            try {
              // Look for variations of the meeting info area
              const infoSelectors = [
                '.MeetingType__information',
                '.meetingtype-information',
                '[data-component="MeetingType/Information"]',
                '.meeting-type-information',
                '.event-type-details',
                '.heading-section',
                'h1.main-heading-title'
              ];
              
              infoSelectors.forEach(selector => {
                const elements = iframeDocument.querySelectorAll(selector);
                elements.forEach(el => {
                  try {
                    if (el.parentNode) {
                      el.parentNode.removeChild(el);
                    }
                  } catch (e) {
                    el.style.display = 'none';
                  }
                });
              });
            } catch (e) {
              console.log("Error removing elements:", e);
            }
          }
        }
      } catch (e) {
        // Cross-origin restrictions prevent accessing iframe content
        console.log("Could not access iframe content due to security restrictions");
      }
    };
    
    // Apply immediately
    applyAdjustments();
    
    // And also after a short delay to ensure content is fully loaded
    setTimeout(applyAdjustments, 500);
    
    // Set up interval to periodically check and adjust height
    const interval = setInterval(applyAdjustments, 1000);
    intervalsRef.current.push(interval);
    
    // Stop checking after 10 seconds
    const timeout = setTimeout(() => {
      try {
        clearInterval(interval);
      } catch (e) {
        console.log("Error clearing interval:", e);
      }
    }, 10000);
    timeoutsRef.current.push(timeout);
  };

  return (
    <section 
      id="contact-section"
      ref={sectionRef}
      className="py-20 relative bg-dark-800"
    >
      {/* Background patterns */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gradient overlay background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 to-secondary-900 z-0"></div>
        
        {/* Circuit pattern overlay */}
        <div className="absolute inset-0 opacity-10 z-0">
          <svg width="100%" height="100%" viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" stroke="white" strokeWidth="1">
              <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" />
              <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" />
              <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" />
              <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" />
            </g>
            <g fill="white" opacity="0.5">
              <circle cx="492.5" cy="224.5" r="4" />
              <circle cx="365.5" cy="196.5" r="4" />
              <circle cx="627.5" cy="358.5" r="4" />
              <circle cx="200.5" cy="144.5" r="4" />
            </g>
          </svg>
        </div>
        
        {/* Floating decorative elements */}
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-primary-600/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-secondary-600/10 rounded-full blur-3xl animate-float-delay-1"></div>
      </div>
      
      <div className="container-padding mx-auto relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.7 }}
              className="text-white"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Revolutionize Your Legal Practice?
              </h2>
              <p className="text-lg text-primary-100 mb-8">
                Join the growing number of forward-thinking law firms using Veritect.ai to gain a competitive edge. Schedule a demo today to see our AI-powered solutions in action.
              </p>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-start">
                  <div className="mr-4 text-blue-500 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Personalized Demonstration</h4>
                    <p className="text-gray-300">See how our solutions can be tailored to your specific practice areas.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="mr-4 text-blue-500 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">ROI Assessment</h4>
                    <p className="text-gray-300">Understand the potential time and cost savings for your firm.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="mr-4 text-blue-500 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Implementation Roadmap</h4>
                    <p className="text-gray-300">Get a clear plan for seamless integration with your existing systems.</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Button
                  variant="primary"
                  size="lg"
                  href="#demo-calendly"
                  className="bg-blue-600 hover:bg-blue-700 shadow-lg relative overflow-hidden group"
                >
                  <span className="relative z-10">Schedule a Demo</span>
                  <span className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                </Button>
                
                <Button
                  variant="outline"
                  size="lg"
                  className="text-white border-white hover:bg-dark-700"
                  href="mailto:info@veritect.ai"
                >
                  Contact Sales
                </Button>
              </div>
            </motion.div>
            
            {/* Right Side - Demo Form Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              id="demo-calendly"
              className="relative"
            >
              <div className="bg-white rounded-xl overflow-hidden shadow-xl">
                {/* Custom-styled header - using primary/secondary gradient instead of black */}
                <div className="bg-gradient-to-r from-primary-600 to-secondary-600 p-4 flex justify-between items-center">
                  <h3 className="text-xl font-bold text-white">Book Your Demo</h3>
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                </div>
                
                {/* Calendly iframe container */}
                <div className={`relative calendar-container ${iframeLoaded ? 'iframe-loaded' : ''}`}>
                  {/* Loading indicator */}
                  <div className="iframe-loading absolute inset-0 flex items-center justify-center bg-white">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                      <p className="mt-4 text-dark-500">Loading calendar...</p>
                    </div>
                  </div>
                  
                  {/* Direct iframe to Calendly with dynamic height */}
                  <iframe
                    ref={iframeRef}
                    id="demo-iframe"
                    src={calendlyUrl}
                    onLoad={handleIframeLoad}
                    allow="camera; microphone; autoplay; clipboard-write; fullscreen"
                    title="Schedule your demo"
                    style={{ height: `${iframeHeight}px` }}
                  />
                </div>
              </div>
              
           
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;